import { useEffect, useState } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { ApiError, getLibraryContent, mediaUrl, type RemoteVideo } from "./api";
import "./content-reader.css";
export function ContentReader({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) {
  const [content, setContent] = useState<RemoteVideo | null>(null),
    [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0),
    [index, setIndex] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setContent(null);
    setError("");
    setIndex(0);
    getLibraryContent(id, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setContent(data);
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(
            e instanceof ApiError && e.status === 404
              ? "内容已下架或不存在"
              : e instanceof Error
                ? e.message
                : "读取失败",
          );
      });
    return () => controller.abort();
  }, [id, attempt]);
  const images = content?.images || [];
  return (
    <section
      className="content-reader"
      role="dialog"
      aria-modal="true"
      aria-label={content?.kind === "image" ? "图集阅读" : "文章阅读"}
    >
      <header>
        <button aria-label="返回内容列表" onClick={onClose}>
          <ArrowLeft size={22} />
          返回
        </button>
        <span>{content?.kind === "image" ? "图集" : "文章"}</span>
      </header>
      {!content && !error && <p role="status">正在读取内容…</p>}
      {error && (
        <div className="reader-error" role="alert">
          <p>{error}</p>
          <button onClick={() => setAttempt((n) => n + 1)}>重试</button>
        </div>
      )}
      {content && (
        <div className="reader-scroll">
          <h1>{content.title}</h1>
          <p className="reader-meta">
            {content.creator} · {content.contentType}
          </p>
          {content.description && (
            <p className="reader-intro">{content.description}</p>
          )}
          {content.kind === "image" ? (
            <div className="reader-gallery">
              {images[index] && (
                <figure>
                  <img
                    src={mediaUrl(images[index].url)}
                    alt={images[index].caption || `第 ${index + 1} 张图片`}
                  />
                  <figcaption>{images[index].caption}</figcaption>
                </figure>
              )}
              <div className="reader-image-controls">
                <button
                  aria-label="上一张"
                  disabled={index === 0}
                  onClick={() => setIndex((n) => n - 1)}
                >
                  <ChevronLeft />
                </button>
                <span>
                  {index + 1} / {images.length}
                </span>
                <button
                  aria-label="下一张"
                  disabled={index >= images.length - 1}
                  onClick={() => setIndex((n) => n + 1)}
                >
                  <ChevronRight />
                </button>
              </div>
              <div className="reader-thumbnails">
                {images.map((image, i) => (
                  <button
                    aria-label={`查看第 ${i + 1} 张`}
                    aria-pressed={i === index}
                    key={image.url + i}
                    onClick={() => setIndex(i)}
                  >
                    <img src={mediaUrl(image.url)} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <article className="reader-article">{content.body}</article>
          )}
        </div>
      )}
    </section>
  );
}
